import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AppUser } from 'Src/views/App/App';
import { getSltAccess, getSltArchive, getSltPeriods } from 'Src/helpers/service/slt';
import { insightsRequest, insightsError } from 'Src/helpers/service/sltInsights';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { InsightsToolbar } from './InsightsToolbar';
import { GenderChart, Sparkline } from './InsightCharts';
import './SltInsights.scss';
import './SltOverview.scss';
import { OverviewContent, overviewPeriodContext } from './OverviewContent';
import { ClubsContent, ClubDrawerContent, ClubsExport, clubHeader } from './ClubsContent';
import './SltClubs.scss';
import { SportContent } from './SportContent';
import { StudentsContent, StudentDrawer } from './StudentsContent';
import { useSavedViews } from './SavedViews';
import { overviewPercent } from './OverviewFormatting';
import { ParticipationSummary } from './ParticipationSummary';
import { printSltReport } from './printReport';
import { comparisonUnavailableReason, isUnavailableComparisonError, withoutComparison } from './ComparisonAvailability';

const titles = {
  overview: 'Overview',
  clubs: 'Clubs',
  sport: 'Sport',
  students: 'Students',
  reports: 'Reports',
  settings: 'Thresholds & alerts'
};
const subtitles = {
  overview: 'Who is taking part, who is missing out, and where to look next.',
  clubs: 'Understand the reach of your clubs and where a closer look would help.',
  sport: 'Teams, fixtures and who represents the school',
  students: 'Ready-made groups first, filters when you need them',
  reports: 'Keep a record of your findings and prepare evidence for school leadership.',
  settings: 'Define participation tiers and the signals your school wants to follow.'
};
const pct = value => (value == null ? '—' : `${Number(value.toFixed(1))}%`);
const number = value => (value == null ? '—' : Number(value.toFixed(1)).toLocaleString('en-GB'));
const date = value =>
  value
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : 'No recorded participation';
const tierNames = ['Not yet', 'Occasional', 'Regular', 'Committed'];
const queryKeys = [
  'from',
  'to',
  'population',
  'archiveAt',
  'snapshotId',
  'area',
  'years',
  'characteristic',
  'characteristic2',
  'segment',
  'page',
  'sort',
  'clubId',
  'sportId',
  'eventId',
  'tournamentId',
  'measure',
  'direction',
  'tableYear',
  'tableGroup'
];
function download(filename: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' })),
    link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function Stepper({
  label,
  value,
  min = 1,
  max = 100,
  disabled = false,
  onChange
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <span className="sli-stepper">
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        value={value}
        readOnly={disabled}
        onChange={event => onChange(Number(event.target.value))}
      />
      <button
        type="button"
        aria-label={`Increase ${label}`}
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        +
      </button>
    </span>
  );
}
function Panel({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="sli-panel">
      <div className="sli-panelHead">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
export function SltInsights({ user }: { user: AppUser }) {
  const history = useHistory(),
    location = useLocation(),
    params = new URLSearchParams(location.search);
  const part = location.pathname.split('/')[2] || 'overview',
    view = titles[part] ? part : 'overview';
  const identity = `${user.activeSchoolId}:${user.roleSessionKey}`,
    key = `${identity}:${location.pathname}:${location.search}`;
  const [meta, setMeta] = React.useState<any>(null),
    [result, setResult] = React.useState<any>(null),
    [reload, setReload] = React.useState(0);
  const [drawer, setDrawer] = React.useState<any>(null),
    [drawerError, setDrawerError] = React.useState(''),
    [busy, setBusy] = React.useState(false);
  const [density, setDensity] = React.useState('calm'),
    [details, setDetails] = React.useState<string[]>([]),
    [selected, setSelected] = React.useState<string[]>([]);
  const [settings, setSettings] = React.useState<any>(null),
    [draft, setDraft] = React.useState<any>(null),
    [notice, setNotice] = React.useState(''),
    [savedName, setSavedName] = React.useState('');
  const [periodOpenSignal, setPeriodOpenSignal] = React.useState(0);
  const [teachingYearSelection, setTeachingYearSelection] = React.useState<{ scope: string; yearId: string } | null>(null);
  const currentKey = React.useRef(key);
  currentKey.current = key;
  const metadata = meta?.identity === identity ? meta : null,
    data = result?.key === key ? result.data : null,
    error = result?.key === key ? result.error : null;
  const list = result?.key === key ? result.list : null,
    comparison = result?.key === key ? result.comparison : null;
  const settingsReady = result?.key === key && result.settingsReady;
  const academicYears = (metadata?.periods?.calendar?.years || []).slice().sort((a, b) => b.start.localeCompare(a.start));
  const reportingFrom = params.get('from');
  const today = metadata?.periods?.today;
  const reportingYear = academicYears.find(year => reportingFrom && year.start <= reportingFrom && year.end >= reportingFrom);
  const currentYear = academicYears.find(year => today && year.start <= today && year.end >= today);
  const teachingYearScope = `${identity}:${reportingYear?.id || ''}`;
  const selectedYearId = teachingYearSelection?.scope === teachingYearScope ? teachingYearSelection.yearId : null;
  const setupYear = academicYears.find(year => year.id === selectedYearId) || reportingYear || currentYear || academicYears[0];
  const yearTargets = draft?.annualTargets?.find(item => item.yearId === setupYear?.id);
  const previousSetupYear = setupYear && academicYears.find(year =>
    year.end < setupYear.start && Number(year.start.slice(0, 4)) === Number(setupYear.start.slice(0, 4)) - 1);
  const previousTargets = draft?.annualTargets?.find(item => item.yearId === previousSetupYear?.id);
  function updateYearTargets(values: { attendance?: number | null; registerCoverage?: number | null }) {
    if (!setupYear) return;
    setDraft(current => {
      const existing = current.annualTargets || [];
      const selected = existing.find(item => item.yearId === setupYear.id);
      const next = { attendance: null, registerCoverage: null, ...selected, ...values, yearId: setupYear.id };
      const others = existing.filter(item => item.yearId !== setupYear.id);
      return { ...current, annualTargets: next.attendance == null && next.registerCoverage == null ? others : [...others, next] };
    });
  }
  const setupTerms = (settings?.terms || []).filter(term => term.yearId === setupYear?.id)
    .sort((a, b) => a.start.localeCompare(b.start));
  React.useEffect(() => {
    if (view !== 'overview' || !data) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent('slt-insights-alerts', {
        detail: { schoolId: user.activeSchoolId, count: data.alerts.filter(item => item.state === 'open').length,
          search:location.search,title:data.alerts.find(item=>item.state==='open')?.title || '' }
      })
    );
  }, [data, user.activeSchoolId, view, location.search]);
  const handledAlertNavigation = React.useRef('');
  React.useEffect(() => {
    const request = `${identity}:${location.key}`;
    if (view !== 'overview' || !data || location.hash !== '#slt-needs-attention' || handledAlertNavigation.current === request) return;
    handledAlertNavigation.current = request;
    const panel = document.getElementById('slt-needs-attention');
    panel?.scrollIntoView({block:'center'});
    panel?.focus({preventScroll:true});
    const open = data.alerts.filter(item=>item.state==='open');
    if (open.length === 1) {setDrawerError('');setDrawer({kind:'alert',value:open[0]});}
  }, [data, identity, view, location.key, location.hash]);
  const canStudents = metadata?.access?.includes('SLT_STUDENT_DRILLDOWN'),
    isDetailed = density === 'detailed';
  const apiQuery = () => {
    const query: any = {};
    queryKeys.forEach(name => {
      if (params.get(name)) {
        query[name] = params.get(name);
      }
    });
    if (view === 'students') {
      query.measure = 'participation';
      if (!query.segment) {
        query.segment = 'notyet';
      }
    }
    query.area = view === 'clubs' ? 'clubs' : view === 'sport' ? 'sport' : params.get('area') || 'all';
    if (view !== 'students') {
      delete query.segment;
      delete query.page;
      delete query.clubId;
      delete query.sportId;
    }
    if (data?.population) {
      query.population = data.population;
      if (data.archive?.observedAt) query.archiveAt = data.archive.observedAt;
      else delete query.archiveAt;
    }
    if (params.get('compare') && params.get('compare') !== 'none' && comparison?.primarySelection)
      Object.assign(query, comparison.primarySelection);
    return query;
  };
  const change = (changes: any, path = location.pathname) => {
    let next = new URLSearchParams(location.search);
    Object.keys(changes).forEach(name =>
      changes[name] == null || changes[name] === '' ? next.delete(name) : next.set(name, String(changes[name]))
    );
    if (!Object.prototype.hasOwnProperty.call(changes, 'snapshotId')) {
      next.delete('snapshotId');
    }
    setNotice('');
    if ((changes.from || changes.to) && metadata?.periods) {
      const reason = comparisonUnavailableReason(metadata.periods, next.get('from'), next.get('to'), next.get('compare'));
      if (reason) {
        next = withoutComparison(next);
        setNotice(`${reason} Comparison set to None; showing the selected period only.`);
      }
    }
    setDrawer(null);
    setDrawerError('');
    history.push({ pathname: path, search: next.toString() ? '?' + next.toString() : '' });
  };
  const [clubsReportSelection, setClubsReportSelection] = React.useState<any>({});
  const [sportReportSelection, setSportReportSelection] = React.useState<any>({});
  const [sportExportSignal, setSportExportSignal] = React.useState(0);
  const students = (changes: any = {}) =>
    change(
      {
        page: 1,
        segment: 'all',
        clubId: null,
        sportId: null,
        eventId: null,
        tournamentId: null,
        area: apiQuery().area,
        population: apiQuery().population,
        archiveAt: apiQuery().archiveAt || null,
        snapshotId: data?.snapshotId,
        ...changes
      },
      '/slt/students'
    );
  React.useEffect(() => {
    if (!drawer) {
      return;
    }
    const previous = document.activeElement as HTMLElement,
      dialog = document.querySelector('.sli-drawer') as HTMLElement;
    if (!dialog) {
      return;
    }
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'SLT details');
    dialog.tabIndex = -1;
    const focusable = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]'
        )
      ).filter(el => !!el.offsetParent);
    (focusable()[0] || dialog).focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setDrawer(null);
        setDrawerError('');
      }
      if (event.key === 'Tab') {
        const items = focusable(),
          first = items[0],
          last = items[items.length - 1];
        if (!first) {
          event.preventDefault();
          dialog.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      if (previous?.isConnected) {
        previous.focus();
      }
    };
  }, [!!drawer]);
  React.useEffect(() => {
    let active = true;
    setMeta(null);
    Promise.all([getSltAccess(user), getSltPeriods(user), getSltArchive(user)])
      .then(([access, periods, archive]) => {
        if (active) {
          setMeta({ identity, access: access.capabilities, periods, archives: archive.snapshots });
        }
      })
      .catch(() => {
        if (active) {
          setMeta({ identity, error: 'School access or calendar could not be loaded.' });
        }
      });
    try {
      setDensity(localStorage.getItem(`slt-density:${user.userId}:${user.activeSchoolId}`) || 'calm');
    } catch {}
    return () => {
      active = false;
    };
  }, [identity, reload]);
  React.useEffect(() => {
    if (!metadata?.periods || params.get('from')) {
      return;
    }
    const preset =
      metadata.periods.presets.find(item => item.id === 'academic-current' && !item.disabledReason) ||
      metadata.periods.presets.find(item => item.from && item.to && !item.disabledReason);
    if (preset) {
      const next = new URLSearchParams(location.search);
      next.set('from', preset.from);
      next.set('to', preset.to);
      next.set('period', preset.id);
      if (!next.has('compare')) next.set('compare',
        comparisonUnavailableReason(metadata.periods, preset.from, preset.to, 'year') ? 'none' : 'year');
      history.replace({ pathname: location.pathname, search: '?' + next.toString() });
    }
  }, [metadata, key]);
  React.useEffect(() => {
    if (!metadata?.periods || !params.get('from') || params.has('compare') || view === 'settings') {
      return;
    }
    const next = new URLSearchParams(location.search);
    next.set('compare', comparisonUnavailableReason(metadata.periods, params.get('from'), params.get('to'), 'year') ? 'none' : 'year');
    history.replace({ pathname: location.pathname, search: '?' + next.toString() });
  }, [metadata, key]);
  React.useEffect(() => {
    setDrawer(null);
    setSelected([]);
    if (!metadata?.access || (view !== 'settings' && !params.get('from'))) {
      return;
    }
    if (view !== 'settings' && !params.has('compare')) return; // Wait for the default selection above.
    if (view !== 'settings' && params.get('compare') !== 'none') {
      const reason = comparisonUnavailableReason(metadata.periods, params.get('from'), params.get('to'), params.get('compare'));
      if (reason) {
        setNotice(`${reason} Comparison set to None; showing the selected period only.`);
        history.replace({ pathname: location.pathname, search: '?' + withoutComparison(params).toString() });
        return;
      }
    }
    let active = true;
    const query = apiQuery();
    setResult(null);
    const load = async () => {
      if (view === 'settings') {
        const [value, ruleData] = await Promise.all([
          insightsRequest(user, 'settings'),
          insightsRequest(user, '', query).catch(() => null)
        ]);
        if (active) {
          setSettings(value);
          setDraft(JSON.parse(JSON.stringify(value.value)));
          setResult({ key, settingsReady: true, data: ruleData });
        }
        return;
      }
      let data: any;
      const extra: any = {};
      if (params.get('compare') && params.get('compare') !== 'none') {
        try {
          extra.comparison = await insightsRequest(user, 'comparison', { ...query, compare: params.get('compare') });
        } catch (error) {
          if (isUnavailableComparisonError(error)) {
            if (active) {
              setNotice('Comparison data is not available for these periods. Comparison set to None; showing the selected period only.');
              history.replace({ pathname: location.pathname, search: '?' + withoutComparison(params).toString() });
            }
            return;
          }
          throw error;
        }
        data = extra.comparison.primary;
      } else data = await insightsRequest(user, '', query);
      if (view === 'students' && canStudents) {
        extra.list = await insightsRequest(user, 'students', { ...query, ...extra.comparison?.primarySelection });
      }
      if (view === 'reports') {
        const [views, log] = await Promise.all([insightsRequest(user, 'views'), insightsRequest(user, 'review-log')]);
        extra.views = views;
        extra.log = log;
      }
      if (active) {
        setResult({ key, data, ...extra });
      }
    };
    load().catch(e => {
      if (active) {
        setResult({ key, error: insightsError(e) });
      }
    });
    return () => {
      active = false;
    };
  }, [key, metadata, reload]);
  const densityChange = (value: string) => {
    setDensity(value);
    try {
      localStorage.setItem(`slt-density:${user.userId}:${user.activeSchoolId}`, value);
    } catch {}
  };
  const savedViews = useSavedViews({ user, tab: view, params, density, onDensity: densityChange });
  const more = (name: string) => !details.includes('-' + name) && (isDetailed || details.includes(name));
  const toggle = (name: string) => (
    <button
      type="button"
      className="sli-button sli-detailButton"
      onClick={() =>
        setDetails([...details.filter(item => item !== name && item !== '-' + name), more(name) ? '-' + name : name])
      }
    >
      {more(name) ? 'Less detail' : 'More detail'}
    </button>
  );
  const openStudent = async (id: string) => {
    const requestedKey = key;
    setDrawer({ kind: 'student', loading: true });
    setDrawerError('');
    try {
      const value = await insightsRequest(user, `students/${id}`, {
        ...apiQuery(),
        measure: 'participation',
        snapshotId: data.snapshotId
      });
      if (currentKey.current === requestedKey) {
        setDrawer({ kind: 'student', value });
      }
    } catch (e) {
      if (currentKey.current === requestedKey) {
        setDrawerError(insightsError(e));
      }
    }
  };
  const alertAction = async (action: string) => {
    setBusy(true);
    setDrawerError('');
    try {
      await insightsRequest(user, `alerts/${drawer.value.key}/${action}`, apiQuery(), 'post', {
        note: drawer.note || ''
      });
      setDrawer(null);
      setReload(value => value + 1);
    } catch (e) {
      setDrawerError(insightsError(e));
    } finally {
      setBusy(false);
    }
  };
  const saveSettings = async () => {
    setBusy(true);
    setNotice('');
    try {
      const value = await insightsRequest(user, 'settings', {}, 'put', { revision: settings.revision, value: draft });
      setSettings(value);
      setDraft(JSON.parse(JSON.stringify(value.value)));
      setNotice('School settings saved.');
      setReload(value => value + 1);
    } catch (e) {
      setNotice(insightsError(e));
    } finally {
      setBusy(false);
    }
  };
  const saveView = async () => {
    setBusy(true);
    setDrawerError('');
    try {
      await insightsRequest(user, 'views', {}, 'post', { name: savedName, query: apiQuery() });
      setDrawer(null);
      setSavedName('');
      setReload(value => value + 1);
      setNotice('View saved with its captured aggregate figures.');
    } catch (e) {
      setDrawerError(insightsError(e));
    } finally {
      setBusy(false);
    }
  };
  const sourceKpis = (sport: boolean) => {
    const reach = data.kpis.find(item => item.key === 'reach');
    const girls = data.equity.find(item => item.field === 'gender')?.group;
    const pp = data.equity.find(item => item.field === 'pupilsPremium')?.group;
    const items = sport
      ? [
          {
            label: 'Represented school',
            value: number(data.sourceBreakdown.represented),
            caption: 'Recorded participation in inter-school events',
            action: () => students({ area: 'sport' })
          },
          {
            label: 'Inter-school events',
            value: number(data.sourceBreakdown.externalFixtures),
            caption: 'Ended events in this period',
            action: () => setDrawer({ kind: 'how' })
          },
          {
            label: 'Girls in sport',
            value: pct(girls?.rate),
            caption:
              girls?.participating == null
                ? 'Unknown or protected'
                : number(girls.participating) + ' of ' + number(girls.total) + ' girls',
            action: () => students({ area: 'sport', characteristic: 'gender:FEMALE' })
          },
          {
            label: 'PP in sport',
            value: pct(pp?.rate),
            caption:
              pp?.participating == null
                ? 'Unknown or protected'
                : number(pp.participating) + ' of ' + number(pp.total) + ' known PP students',
            action: () => students({ area: 'sport', characteristic: 'pupilsPremium:yes' })
          }
        ]
      : [
          {
            label: 'Students in a club',
            value: pct(reach?.value),
            caption:
              reach?.count == null
                ? 'Awaiting participation evidence'
                : number(reach.count) + ' of ' + number(reach.total) + ' students',
            action: () => students({ area: 'clubs' })
          },
          {
            label: 'Capacity used',
            value: '—',
            caption: 'Review attendance and capacity for each club',
            action: () => document.getElementById('sli-clubs-table')?.scrollIntoView({ behavior: 'smooth' })
          },
          {
            label: 'Clubs with attendance gaps',
            value: number(data.clubs.filter(club => club.sessions > club.markedSessions).length),
            caption: 'Check the session evidence',
            action: () => document.getElementById('sli-clubs-table')?.scrollIntoView({ behavior: 'smooth' })
          },
          {
            label: 'Attendance',
            value: pct(data.attendance?.value),
            caption: data.attendance?.suppressed
              ? 'Protected attendance figures'
              : data.attendance?.marked
              ? number(data.attendance.present) + ' present / ' + number(data.attendance.marked) + ' marked entries'
              : 'No attendance marks',
            action: () => setDrawer({ kind: 'how' })
          }
        ];
    return (
      <div className="sli-kpis">
        {items.map(item => (
          <button type="button" className="sli-kpi" key={item.label} onClick={item.action}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <small>{item.caption}</small>
            <span className="sli-kpiChevron" aria-hidden="true">
              ›
            </span>
          </button>
        ))}
      </div>
    );
  };
  const activityTable = (sport = false, attentionOnly = false) => (
    <div className="sli-tableWrap">
      <table className="sli-table">
        <thead>
          <tr>
            {(sport
              ? ['Sport', 'Teams', 'Players', 'Girls', 'PP', 'Events', 'Results', '']
              : ['Club', 'Activity / lead', 'Students', 'Avg attendance', 'Capacity now', 'Last sessions', 'Signal', '']
            ).map((label, index) => (
              <th key={index}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(sport ? data.sports : data.clubs.filter(club => !attentionOnly || club.sessions > club.markedSessions)).map(
            item => (
              <tr key={item.id}>
                <th>
                  <button
                    type="button"
                    className="sli-link"
                    onClick={() => setDrawer({ kind: sport ? 'sport' : 'club', value: item })}
                  >
                    {item.name}
                  </button>
                </th>
                {sport ? (
                  <>
                    <td>{item.teams}</td>
                    <td>{number(item.players)}</td>
                    <td>{pct(item.girlsPercent)}</td>
                    <td>{pct(item.ppPercent)}</td>
                    <td>{item.fixtures}</td>
                    <td>
                      <button
                        type="button"
                        className="sli-link"
                        onClick={() =>
                          document.getElementById('slt-sport-results')?.scrollIntoView({ behavior: 'smooth' })
                        }
                      >
                        Review results ↓
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      {item.activity}
                      <small>{item.leader || 'Lead not recorded'}</small>
                    </td>
                    <td>{number(item.students)}</td>
                    <td>{number(item.averageAttendance)}</td>
                    <td>{pct(item.capacityUsed)}</td>
                    <td>
                      <Sparkline series={item.series} />
                    </td>
                    <td>
                      <span className="sli-badge">{item.signal}</span>
                    </td>
                  </>
                )}
                <td>
                  <button
                    type="button"
                    className="sli-link"
                    disabled={!canStudents || item.suppressed}
                    onClick={() =>
                      students({
                        area: sport ? 'sport' : 'clubs',
                        ...(sport ? { sportId: item.id } : { clubId: item.id })
                      })
                    }
                  >
                    Students →
                  </button>
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>
      {!(sport ? data.sports : data.clubs).length && (
        <p className="sli-empty">No {sport ? 'sport events' : 'clubs'} are available for this reporting period.</p>
      )}
    </div>
  );
  const overview = () => (
    <OverviewContent
      data={data}
      comparison={comparison}
      periods={metadata?.periods}
      detailed={isDetailed}
      details={details}
      canStudents={canStudents}
      onToggle={name => setDetails(details.includes(name) ? details.filter(item => item !== name) : [...details, name])}
      onStudents={filters => (canStudents ? students(filters) : setDrawer({ kind: 'how' }))}
      onDrawer={value => {
        setDrawerError('');
        setDrawer(value);
      }}
      onNavigate={path => change({}, path)}
      onDetailed={() => densityChange('detailed')}
      onExport={() => setDrawer({ kind: 'export', segment: 'notyet' })}
      onPeriodChange={() => setPeriodOpenSignal(value=>value+1)}
    />
  );
  const studentPage = () => (
    <StudentsContent
      user={user}
      query={{ ...apiQuery(), snapshotId: data.snapshotId }}
      params={params}
      data={data}
      list={list}
      onChange={change}
      onStudent={openStudent}
      onReport={() => setDrawer({ kind: 'participation-summary' })}
      onSetup={() => change({}, '/slt/settings/thresholds')}
      onMessage={value => setDrawer({ kind: 'generalMessages', ...value })}
    />
  );
  const settingsPage = () =>
    draft && settings ? (
      <>
        <Panel title="Participation tiers">
          <p className="sli-help">
            Average session enrolments per selected teaching half-term. Club membership alone does not establish session
            frequency. Holiday periods must not be selected.
          </p>
          <div className="sli-tierSettings">
            {['First recorded activity', 'Regular from', 'Committed from'].map((label, index) => (
              <label key={label}>
                {label}
                <Stepper
                  label={label}
                  value={draft.tiers[index]}
                  disabled={index === 0}
                  onChange={value =>
                    setDraft({ ...draft, tiers: draft.tiers.map((v, i) => (i === index ? value : v)) })
                  }
                />
                <small>{index === 0 ? 'Any recorded participation' : 'sessions / half-term'}</small>
              </label>
            ))}
          </div>
          <label className="sli-settingLabel">
            Minimum group size
            <input
              type="number"
              min={10}
              max={100}
              value={draft.suppression}
              onChange={event => setDraft({ ...draft, suppression: Number(event.target.value) })}
            />
          </label>
        </Panel>
        <Panel title="Club decisions">
          {[
            ['minimumSize', 'Minimum viable club size'],
            ['thrivingGrowth', 'Thriving growth (%)'],
            ['thrivingCapacity', 'Thriving capacity (%)']
          ].map(([field, label]) => (
            <label className="sli-settingLabel" key={field}>
              {label}
              <Stepper
                label={label}
                value={draft.clubCriteria[field]}
                max={field === 'minimumSize' ? 1000 : 100}
                onChange={value => setDraft({ ...draft, clubCriteria: { ...draft.clubCriteria, [field]: value } })}
              />
            </label>
          ))}
        </Panel>
        <Panel title="Academic year targets">
          {setupYear && (
            <label className="sli-settingLabel">
              Academic year
              <select className="sli-select" value={setupYear.id}
                onChange={event => setTeachingYearSelection({ scope: teachingYearScope, yearId: event.target.value })}>
                {academicYears.map(year => <option key={year.id} value={year.id}>{year.name}</option>)}
              </select>
            </label>
          )}
          {setupYear ? <>
            <p className="sli-help">Set goals for {setupYear.name}. Teaching half-terms below use the same academic year.</p>
            {([
              ['attendance', 'Attendance target (%)'],
              ['registerCoverage', 'Register coverage target (%)']
            ] as const).map(([field, label]) => (
              <label className="sli-settingLabel" key={field}>
                {label}
                <input type="number" min={0} max={100} step={1} placeholder="No target set"
                  value={yearTargets?.[field] ?? ''}
                  onChange={event => updateYearTargets({ [field]: event.target.value === '' ? null : event.target.valueAsNumber })} />
              </label>
            ))}
            {previousTargets && (previousTargets.attendance != null || previousTargets.registerCoverage != null) && (
              <button type="button" className="sli-button" onClick={() => updateYearTargets({
                attendance: previousTargets.attendance,
                registerCoverage: previousTargets.registerCoverage
              })}>Copy targets from {previousSetupYear.name}</button>
            )}
          </> : <p className="sli-help">Add an academic year in Admin console → Term dates to set targets.</p>}
        </Panel>
        <Panel title="Teaching half-terms">
          {setupYear && <p className="sli-help" role="status">
            {!setupTerms.length
              ? `No term dates are configured for ${setupYear.name}. Ask your school administrator to add this year's dates in Admin console → Term dates, then select the teaching half-terms here.`
              : `${setupTerms.filter(term => draft.teachingTermIds.includes(term.id)).length} teaching half-terms selected for ${setupYear.name}.`}
          </p>}
          <p className="sli-help">
            Choose the actual teaching blocks from your school calendar. Breaks stay available as reporting periods but
            must not enter the tier denominator.
          </p>
          <div className="sli-termChecks">
            {setupTerms.map(term => (
              <label key={term.id} className="sli-check">
                <input
                  type="checkbox"
                  checked={draft.teachingTermIds.includes(term.id)}
                  onChange={() =>
                    setDraft({
                      ...draft,
                      teachingTermIds: draft.teachingTermIds.includes(term.id)
                        ? draft.teachingTermIds.filter(id => id !== term.id)
                        : [...draft.teachingTermIds, term.id]
                    })
                  }
                />
                <span>
                  {term.name}
                  <small>
                    {term.start} – {term.end}
                  </small>
                </span>
              </label>
            ))}
          </div>
          {!setupYear && (
            <p className="sli-empty">Set up academic years and term dates in the school’s Academic calendar first.</p>
          )}
        </Panel>
        <Panel title="Alert rules">
          <p className="sli-help">
            Equity, sport gender, club attendance, waiting-list and missing-register signals use recorded evidence.
            Participation rules use participant lists and teaching weeks, excluding holidays. When the selected period
            lacks the required evidence, an enabled rule waits until its conditions can be assessed.
          </p>
          {Object.keys(draft.rules).map(name => {
            const rule = draft.rules[name];
            return (
              <div className="sli-rule" key={name}>
                <label className="sli-check">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={rule.enabled}
                    onChange={event =>
                      setDraft({
                        ...draft,
                        rules: { ...draft.rules, [name]: { ...rule, enabled: event.target.checked } }
                      })
                    }
                  />
                  <span>
                    {
                      {
                        equity: 'Equity gap',
                        registers: 'Ended sessions without attendance',
                        stopped: 'Stopped taking part',
                        never: 'No recorded participation',
                        ydecline: 'Year-group decline',
                        gender: 'Girls / boys gap',
                        club: 'Club attendance decline',
                        waitlist: 'Waiting-list pressure'
                      }[name]
                    }
                    <small>
                      {name === 'equity'
                          ? 'Percentage-point gap between known groups'
                          : name === 'gender'
                          ? 'Percentage-point gap between girls and boys'
                          : name === 'club'
                          ? 'Percentage decline across four recorded weeks'
                          : name === 'waitlist'
                          ? 'Consecutive weeks at current capacity'
                          : name === 'stopped'
                          ? 'Teaching weeks without participation after a regular completed half-term'
                          : name === 'never'
                          ? 'Verified teaching weeks of membership without participation in this period'
                          : name === 'ydecline'
                          ? 'Percentage-point drop between two completed half-terms, over equal teaching days'
                          : 'Days after a session ends'}
                    </small>
                    {rule.enabled && data?.ruleAvailability?.[name]?.ready === false && (
                      <small>{data.ruleAvailability[name].reason}</small>
                    )}
                  </span>
                </label>
                <Stepper
                  label={`${name} threshold`}
                  value={rule.value}
                  max={365}
                  onChange={value => setDraft({ ...draft, rules: { ...draft.rules, [name]: { ...rule, value } } })}
                />
              </div>
            );
          })}
        </Panel>
        <div className="sli-savebar">
          <span role="status">{notice || 'School-wide dashboard settings'}</span>
          <button
            type="button"
            className="sli-button"
            onClick={() => {
              setDraft(JSON.parse(JSON.stringify(settings.value)));
              setNotice('');
            }}
          >
            Cancel changes
          </button>
          <button
            type="button"
            className="sli-button sli-primary"
            disabled={busy || JSON.stringify(draft) === JSON.stringify(settings.value)}
            onClick={saveSettings}
          >
            Save settings
          </button>
        </div>
      </>
    ) : (
      <p>Loading settings…</p>
    );
  const reportsPage = () => (
    <>
      <Panel title="SLT reports">
        <p>Current filters, participation and period comparison, with historical-cohort and data-quality notes.</p>
          <div className="sli-reportActions">
            <button className="sli-button sli-primary" onClick={() => setDrawer({ kind: 'participation-summary' })}>
              Participation summary
            </button>
            <button
              className="sli-button"
              onClick={() => setDrawer({ kind: 'participation-summary', reportKind: 'clubs' })}
            >
              Clubs report
            </button>
            <button
              className="sli-button"
              onClick={() => setDrawer({ kind: 'participation-summary', reportKind: 'sport' })}
            >
              Sport report
            </button>
          </div>
      </Panel>
      {savedViews.reports}
      <div className="sli-reportCards">
        {[
          ['Governor briefing', 'Participation, equity and the evidence behind the figures.'],
          ['Inspection evidence', 'A factual participation summary with source limitations.'],
          ['PE & sport review', 'Sport reach and the balance of opportunities.']
        ].map(([title, body]) => (
          <Panel key={title} title={title}>
            <p>{body}</p>
            <p className="sli-help">
              Use the current aggregate view to prepare your evidence. Automated statutory packs and scheduled email
              delivery are not configured.
            </p>
            <button className="sli-button" type="button" onClick={() => setDrawer({ kind: 'report', title })}>
              Prepare summary →
            </button>
          </Panel>
        ))}
      </div>
      <Panel
        title="Captured report snapshots"
        action={
          <button
            className="sli-link"
            type="button"
            onClick={() => {
              setDrawerError('');
              setDrawer({ kind: 'save' });
            }}
          >
            Save current view
          </button>
        }
      >
        {(result.views || []).map(item => (
          <div className="sli-saved" key={item.id}>
            <strong>{item.name}</strong>
            <span>{date(item.createdAt)}</span>
            <button
              type="button"
              className="sli-link"
              onClick={() =>
                change({ ...queryKeys.reduce((all, k) => ({ ...all, [k]: null }), {}), ...item.query }, '/slt/overview')
              }
            >
              Open filters →
            </button>
          </div>
        ))}
        {!result.views?.length && <p className="sli-empty">No saved views yet.</p>}
      </Panel>
      <Panel title="Review log">
        {(result.log || []).map(item => (
          <div className="sli-log" key={item.id}>
            <span className="sli-badge">{item.action}</span>
            <strong>{item.title}</strong>
            <span>{date(item.createdAt)}</span>
            <span>By {item.actorName || 'School leadership'}</span>
            <button
              type="button"
              className="sli-link"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await insightsRequest(user, `alerts/${item.key}/reopen`, apiQuery(), 'post', {});
                  setReload(value => value + 1);
                  setNotice('Signal reopened.');
                } catch (e) {
                  setNotice(insightsError(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Reopen
            </button>
            {item.note && <p>{item.note}</p>}
          </div>
        ))}
        {!result.log?.length && <p className="sli-empty">Reviewed and snoozed alerts will appear here.</p>}
      </Panel>
    </>
  );
  const drawerBody = () => {
    // A route change invalidates data before the effect closes the old drawer.
    // Never render its data-dependent content against the next route's loading state.
    if (!data && !settingsReady) {
      return <p role="status">{error || metadata?.error || 'Loading school participation…'}</p>;
    }
    if (drawer.kind === 'how') {
      return (
        <>
          <p>
            Participation counts club enrolments and named participants of past sessions and sport events. Absence,
            missing marks and conflicting attendance do not remove a registered participant. Positive attendance also
            remains valid evidence for legacy events whose participant list is no longer retained.
          </p>
          <h3>Clubs and Sport</h3>
          <p>
            For a current period, the current active club list counts enrolment even before the first session. It is an
            observation at capture, not a joining date. Past and archived periods use dated session participant lists.
          </p>
          <p>
            Events linked to a club are Clubs. All events without a club link are Sport. “Represented school” includes
            inter-school events only.
          </p>
          <h3>Attendance — separate from participation</h3>
          <p>
            Attendance is present marks divided by explicit present and absent marks for registered pupil-session
            records. Missing and conflicting marks are excluded from that percentage; club enrolment without sessions
            has no attendance denominator.
          </p>
          {data?.attendance && (
            <p>
              Present: {number(data.attendance.present)} · Absent: {number(data.attendance.absent)} · No mark:{' '}
              {number(data.attendance.unknown)} · Conflicting: {number(data.attendance.conflicting)}.
            </p>
          )}
          <h3>Academic year targets</h3>
          {[['Selected period', data], ['Comparison period', comparison?.comparison]]
            .filter(([, aggregate]) => aggregate?.targets)
            .map(([label, aggregate]) => {
              const { yearName, attendance, registerCoverage } = aggregate.targets;
              const formatTarget = (value: number | null) => value == null ? 'No target set' : pct(value);
              return <p key={label}>{label}{yearName ? ` (${yearName})` : ''} · Attendance: {pct(aggregate.attendance?.value)} (target: {formatTarget(attendance)}) · Register coverage: {pct(aggregate.coverage?.value)} (target: {formatTarget(registerCoverage)})</p>;
            })}
          {comparison?.cohortLabels && <>
            <h3>Comparison rosters</h3>
            <p>Selected period: {comparison.cohortLabels.primary}. Comparison: {comparison.cohortLabels.comparison}.
              Each period uses its own pupils and year groups. A monthly observation describes its dated roster.</p>
          </>}
          <h3>Retention</h3>
          <p>
            Previous half-term participants who took part again in the next completed teaching half-term, using
            participant lists rather than attendance. Both windows use the same number of teaching days.
            {' '}{data?.kpis.find(item=>item.key==='retention')?.detail || data?.kpis.find(item=>item.key==='retention')?.reason}
          </p>
          <h3>Attendance coverage</h3>
          <p>
            {data?.coverage.marked} of {data?.coverage.scheduled} ended sessions have at least one school attendance
            mark. This is a proxy for recorded attendance; the system has no separate register-submitted marker.
          </p>
          {view === 'clubs' && (
            <>
              <h3>Club capacity and signals</h3>
              <p>
                Participation counts club registration independently of attendance. Capacity used includes only clubs
                with measured attendance and a configured current capacity. Historical capacity changes and past waiting
                lists are not reconstructed. Timetable slots use session start times in the school timezone; 08:45–11:29
                is outside the defined slots. Decline and viability signals require consecutive recorded weeks; missing
                weeks are never treated as zero.
              </p>
            </>
          )}
          <h3>Roster and characteristics</h3>
          <p>
            Current students, verified historical membership and a monthly archived roster are separate populations.
            Unknown traits stay Unknown. Current gender is never substituted into an archived period.
          </p>
          <h3>Privacy and interpretation</h3>
          <p>
            Groups below {data?.suppression || 10} and small outcomes are protected. Missing attendance is not absence.
            Monthly observations do not establish continuous coverage.
          </p>
          {result?.comparisonError && (
            <>
              <h3>Comparison unavailable</h3>
              <p>{result.comparisonError}</p>
            </>
          )}
          <h3>Regular participation</h3>
          <p>
            Thresholds use session enrolments per explicitly selected teaching half-term. Unconfigured teaching blocks
            and unsupported historical comparisons appear as unavailable.
          </p>
        </>
      );
    }
    if (drawer.kind === 'generalMessages') {
      return (
        <>
          <h2>Connect General messages</h2>
          <p>To message {drawer.audience || 'school contacts'}, connect the General messages module for your school.</p>
          {drawer.suggestion && (
            <p>Suggested message: Please help {drawer.student.firstName} find a club that matches their interests.</p>
          )}
          <p className="sli-muted">No messages have been sent.</p>
        </>
      );
    }
    if (drawer.kind === 'student') {
      return drawer.value && data ? (
        <StudentDrawer
          student={drawer.value}
          data={data}
          onActivity={activity => {
            const item = activity.clubId
              ? data.clubsReport?.items.find(item => item.id === activity.clubId)
              : data.sports.find(item => item.id === activity.sportId);
            if (item) {
              setDrawer({ kind: activity.clubId ? 'club' : 'sport', value: item });
            } else {
              setDrawerError('This activity is unavailable in this period.');
            }
          }}
          onMessage={value => setDrawer({ kind: 'generalMessages', ...value })}
        />
      ) : (
        <p>Loading student evidence…</p>
      );
    }
    if (drawer.kind === 'club') {
      if (!data) {
        return <p>Loading club evidence…</p>;
      }
      const club = drawer.value;
      return (
        <ClubDrawerContent
          club={club}
          timezone={data.timezone}
          coverageTarget={data.clubsReport.thresholds.coverageTarget}
          canStudents={canStudents}
          canMessage={!!data.availability.messaging}
          onContact={() => setDrawer({ kind: 'message' })}
          onMembers={() => students({ area: 'clubs', clubId: club.id, segment: 'all' })}
        />
      );
    }
    if (drawer.kind === 'sport') {
      const item = drawer.value,
        sport = drawer.kind === 'sport';
      return (
        <>
          <h2>{item.name}</h2>
          <p>{sport ? 'All events without a club link for this sport.' : item.activity}</p>
          {!sport && (
            <>
              <p>Lead: {item.leader || 'Not recorded'}</p>
              <p>Current capacity: {number(item.maxParticipants)} · historical capacity is unavailable.</p>
            </>
          )}
          <button
            className="sli-button sli-primary"
            type="button"
            disabled={!canStudents || item.suppressed}
            onClick={() =>
              students({ area: sport ? 'sport' : 'clubs', ...(sport ? { sportId: item.id } : { clubId: item.id }) })
            }
          >
            View participating students →
          </button>
          {sport ? (
            <>
              <p>
                {item.external} inter-school events · {item.internal} internal events.
              </p>
              <p className="sli-help">
                Match scores, cricket, placements and tournament achievements appear in the results section below.
              </p>
            </>
          ) : (
            <>
              <h3>Session log</h3>
              {item.sessionLog.map(session => (
                <div className="sli-fact" key={session.id}>
                  <strong>{date(session.at)}</strong>
                  <span>
                    {session.hasMarks ? `${number(session.present)} confirmed present` : 'No school attendance marks'}
                  </span>
                </div>
              ))}
            </>
          )}
        </>
      );
    }
    if (drawer.kind === 'alert') {
      return (
        <>
          <span className="sli-badge">
            {drawer.value.category} · {drawer.value.state}
          </span>
          <h2>{drawer.value.title}</h2>
          <p>{drawer.value.body}</p>
          <p className="sli-callout">
            {drawer.value.threshold}
            {drawer.value.lowConfidence ? ' · Low confidence: inspect attendance coverage.' : ''}
          </p>
          <p>Suggested owner: {drawer.value.owner}</p>
          {(drawer.value.characteristic || drawer.value.segment || drawer.value.years) && (
            <button
              type="button"
              className="sli-button"
              disabled={!canStudents}
              onClick={() =>
                students({
                  ...(drawer.value.characteristic ? {characteristic:drawer.value.characteristic} : {}),
                  ...(drawer.value.years ? {years:drawer.value.years} : {}),
                  ...(['stopped','never','ydecline'].includes(drawer.value.rule) ? {measure:'participation'} : {}),
                  segment: drawer.value.segment,
                  area: drawer.value.area || apiQuery().area
                })
              }
            >
              View matching students →
            </button>
          )}
          <label className="sli-settingLabel">
            Review note
            <textarea
              maxLength={500}
              value={drawer.note || ''}
              onChange={event => setDrawer({ ...drawer, note: event.target.value })}
            />
          </label>
          <div className="sli-drawerActions">
            <button type="button" className="sli-button" disabled={busy} onClick={() => alertAction('snooze')}>
              Snooze 14 days
            </button>
            <button
              type="button"
              className="sli-button sli-primary"
              disabled={busy}
              onClick={() => alertAction('review')}
            >
              Mark reviewed
            </button>
            {drawer.value.state !== 'open' && (
              <button type="button" className="sli-link" disabled={busy} onClick={() => alertAction('reopen')}>
                Reopen
              </button>
            )}
          </div>
        </>
      );
    }
    if (drawer.kind === 'squady') {
      return (
        <>
          <h2>Ask about your data</h2>
          <div className="sli-callout">{data.summary}</div>
          <h3>Try</h3>
          <div className="slo-suggestions">
            <button
              className="sli-button"
              onClick={() => change({ characteristic: 'pupilsPremium:yes' }, '/slt/clubs')}
            >
              Which clubs do PP students go to most?
            </button>
            <button className="sli-button" onClick={() => students({ segment: 'stopped' })}>
              Who stopped coming after half-term?
            </button>
            <button className="sli-button" onClick={() => setDrawer({ kind: 'participation-summary' })}>
              Summarise this term for governors
            </button>
          </div>
          <p className="sli-help">Free-text questions are not connected to a data assistant for this school yet.</p>
          <input placeholder="Ask a question about participation" disabled title="Data assistant is not connected" />
        </>
      );
    }
    if (drawer.kind === 'participation-summary' || drawer.kind === 'export') {
      return (
        <ParticipationSummary
          user={user}
          data={data}
          reportKind={drawer.reportKind || (view === 'clubs' ? 'clubs' : view === 'sport' ? 'sport' : 'summary')}
          selection={view === 'clubs' ? clubsReportSelection : view === 'sport' ? sportReportSelection : {}}
          query={{ ...apiQuery(), snapshotId: data.snapshotId, ...(drawer.segment ? { segment: drawer.segment } : {}) }}
          compare={params.get('compare') || 'none'}
          compareFrom={params.get('compareFrom') || undefined}
          compareTo={params.get('compareTo') || undefined}
          comparisonError={result?.comparisonError}
        />
      );
    }
    if (drawer.kind === 'save') {
      return (
        <>
          <label className="sli-settingLabel">
            View name
            <input maxLength={100} value={savedName} onChange={event => setSavedName(event.target.value)} />
          </label>
          <p className="sli-help">
            Saves the filters and captured aggregate figures. No individual pupil rows are stored in this report.
          </p>
          <button
            type="button"
            className="sli-button sli-primary"
            disabled={busy || !savedName.trim()}
            onClick={saveView}
          >
            Save view
          </button>
          {data && view !== 'settings' && (
            <button
              type="button"
              className="sli-button sli-primary"
              onClick={() => setDrawer({ kind: 'participation-summary' })}
            >
              Generate report
            </button>
          )}
        </>
      );
    }
    if (drawer.kind === 'report' && drawer.scope === 'clubs') {
      const report = data.clubsReport;
      const csv = () => {
        const quote = value =>
          '"' +
          String(value == null ? '' : value)
            .replace(/^[=+@-]/, "'$&")
            .replace(/"/g, '""') +
          '"';
        const columns = [
          'Club',
          'Activity',
          'Slot',
          'Lead',
          'Average attendance',
          'Capacity',
          'Capacity used',
          'Registers taken',
          'Signal'
        ];
        const rows = report.items
          .filter(club => club.sessions)
          .map(club => [
            club.name,
            club.category || club.activity,
            club.scheduleLabel,
            club.leader,
            club.averageAttendance,
            club.maxParticipants,
            club.capacityUsed,
            club.markCoverage,
            club.signal
          ]);
        download('clubs-summary.csv', [columns, ...rows].map(row => row.map(quote).join(',')).join('\r\n'));
      };
      return (
        <ClubsExport
          report={report}
          school={user.activeSchoolName}
          period={`${date(data.period.from)} – ${date(new Date(+new Date(data.period.to) - 1).toISOString())}`}
          onCsv={csv}
        />
      );
    }
    if (drawer.kind === 'report') {
      return (
        <div className="sli-printSummary">
          <h2>{drawer.title}</h2>
          <p>
            {user.activeSchoolName} · {date(data.period.from)} –{' '}
            {date(new Date(+new Date(data.period.to) - 1).toISOString())}
          </p>
          <p>{data.summary}</p>
          {data.kpis.map(item => (
            <p key={item.key}>
              <strong>{item.label}: </strong>
              {item.unit === 'percent' ? pct(item.value) : number(item.value)} {item.reason}
            </p>
          ))}
          <h3>Sources and limitations</h3>
          <p>
            Filters: {[...new Set([params.get('characteristic'), params.get('characteristic2'), params.get('tableGroup')])]
              .filter(Boolean)
              .map(value => ({ 'gender:FEMALE': 'Girls', 'gender:MALE': 'Boys', 'pupilsPremium:yes': 'PP',
                'pupilsPremium:no': 'Non-PP', 'specialEducationNeeds:yes': 'SEND', 'specialEducationNeeds:no': 'Non-SEND',
                'freeSchoolMeals:yes': 'FSM', 'freeSchoolMeals:no': 'Non-FSM' }[value] || value))
              .concat([params.get('years'), params.get('tableYear')].filter(Boolean).map(value => `Year groups: ${value}`))
              .join(' · ') || 'All students'}.
          </p>
          <p>
            {{ current: 'Current school roster', historical: 'Historical school roster', archive: 'Archived school roster' }[
              data.population
            ] || 'School roster'}
            {data.archive?.observedAt ? ` observed ${date(data.archive.observedAt)}` : ''}
            . Club enrolments and event participant lists; attendance is reported separately.
            Attendance marks are a proxy for coverage, not register submission. Small groups are protected. Figures
            captured {date(data.sourceCutoffAt)}.
          </p>
          <button type="button" className="sli-button sli-primary" onClick={event => printSltReport(event.currentTarget)}>
            Print / save as PDF
          </button>
          <p className="sli-help">
            Uses your browser’s print dialog. This is a factual summary, not an automatically generated statutory
            report.
          </p>
        </div>
      );
    }
    return (
      <>
        <h2>Contact through your school’s normal channels</h2>
        <p>
          This SLT role has no operational messaging permission. No messages have been sent. Use a school role with the
          appropriate consent and contact access.
        </p>
      </>
    );
  };
  return (
    <div
      className={
        view === 'overview'
          ? 'slt-insights slt-overview'
          : view === 'clubs'
          ? 'slt-insights slt-clubs'
          : view === 'sport'
          ? 'slt-insights slt-sport'
          : view === 'students'
          ? 'slt-insights slt-students'
          : view === 'reports'
          ? 'slt-insights slt-reports'
          : 'slt-insights'
      }
    >
      <header className="sli-heading">
        <div>
          <h1>{titles[view]}</h1>
          <p>
            {view === 'clubs'
              ? clubHeader(data?.clubsReport, overviewPeriodContext(metadata?.periods, data).split(' · ')[0])
              : view === 'overview'
              ? overviewPeriodContext(metadata?.periods, data)
              : subtitles[view]}
          </p>
        </div>
        <div className="sli-headingActions">
          {view === 'overview' && (
            <div className="sli-density" aria-label="Dashboard detail">
              <button className={!isDetailed ? 'on' : ''} onClick={() => densityChange('calm')}>
                Calm
              </button>
              <button className={isDetailed ? 'on' : ''} onClick={() => densityChange('detailed')}>
                Detailed
              </button>
            </div>
          )}
          {view !== 'settings' && savedViews.header}
          {view === 'sport' ? (
            <button
              className="sli-button"
              disabled={!data}
              onClick={() => setDrawer({ kind: 'participation-summary' })}
            >
              Export report
            </button>
          ) : view === 'clubs' ? (
            <button
              className="sli-button"
              disabled={!data}
              onClick={() => setDrawer({ kind: 'participation-summary' })}
            >
              Export
            </button>
          ) : view === 'overview' ? (
            <button
              className="sli-button sli-primary"
              disabled={!data}
              onClick={() => setDrawer({ kind: 'participation-summary' })}
            >
              Generate report
            </button>
          ) : view === 'students' ? (
            <button
              className="sli-button"
              disabled={!data}
              onClick={() => setDrawer({ kind: 'participation-summary' })}
            >
              Generate report
            </button>
          ) : null}
        </div>
      </header>
      {metadata?.periods && view !== 'settings' && (
        <InsightsToolbar
          formatPercent={view === 'overview' ? overviewPercent : undefined}
          views={savedViews.toolbar}
          params={params}
          periods={metadata.periods}
          archives={metadata.archives}
          data={data}
          comparison={comparison || (result?.comparisonError ? { unavailableReason: result.comparisonError } : null)}
          onChange={change}
          onHow={() => setDrawer({ kind: 'how' })}
          clubsReport={view === 'clubs' ? data?.clubsReport : null}
          openPeriodSignal={periodOpenSignal}
        />
      )}
      {notice && view !== 'settings' && (
        <p role="status" className="sli-callout">
          {notice}
        </p>
      )}
      {(error || metadata?.error) && (
        <div className="sli-error" role="alert">
          {error || metadata.error}
          <button
            type="button"
            className="sli-button"
            onClick={() => (params.has('snapshotId') ? change({ snapshotId: null }) : setReload(value => value + 1))}
          >
            Retry
          </button>
        </div>
      )}
      {!data && !settingsReady && !error && !metadata?.error && (
        <div className="sli-loading" aria-live="polite">
          Loading school participation…
        </div>
      )}
      {(data || settingsReady) && (
        <>
          {data?.archive?.excludedStudents > 0 && (
            <p role="status" className="sli-callout">
              Incomplete archived roster: {data.archive.excludedStudents} unmatched {data.archive.excludedStudents === 1 ? 'pupil is' : 'pupils are'} excluded.
              {' '}All counts and percentages cover matched pupils only.
            </p>
          )}
          {comparison?.comparison?.archive?.excludedStudents > 0 && (
            <p role="status" className="sli-callout">
              The comparison roster is incomplete: {comparison.comparison.archive.excludedStudents} unmatched {comparison.comparison.archive.excludedStudents === 1 ? 'pupil is' : 'pupils are'} excluded.
              {' '}Comparison counts and percentages cover matched pupils only.
            </p>
          )}
          {result.comparisonError && !['overview', 'clubs', 'sport'].includes(view) && (
            <p role="status" className="sli-callout">
              Comparison unavailable: {result.comparisonError}
            </p>
          )}
          {data?.empty && !['overview', 'clubs', 'sport'].includes(view) && (
            <p className="sli-callout">
              No recorded participation evidence is available in this selection. Try another period or check activity
              attendance records.
            </p>
          )}
          {view === 'overview' && overview()}
          {view === 'students' &&
            (canStudents ? (
              studentPage()
            ) : (
              <p>Your SLT access includes aggregate figures. Student detail access has not been granted.</p>
            ))}
          {view === 'settings' && settingsPage()}
          {view === 'reports' && reportsPage()}
          {view === 'clubs' && data.clubsReport && (
            <ClubsContent
              onReportStateChange={setClubsReportSelection}
              report={data.clubsReport}
              comparison={comparison}
              onStudents={query => (canStudents ? students(query) : setDrawer({ kind: 'how' }))}
              onClub={value => setDrawer({ kind: 'club', value })}
              onExport={() => setDrawer({ kind: 'participation-summary' })}
              onPeriod={() => setPeriodOpenSignal(value => value + 1)}
              onSetup={() => change({}, '/slt/settings/thresholds')}
            />
          )}
          {view === 'sport' && (
            <SportContent
              user={user}
              query={{ ...apiQuery(), snapshotId: data.snapshotId }}
              data={data}
              comparison={comparison}
              periodName={overviewPeriodContext(metadata?.periods, data).split(' · ')[0]}
              canStudents={canStudents}
              onStudents={students}
              onStudent={openStudent}
              exportSignal={sportExportSignal}
              onReportStateChange={setSportReportSelection}
            />
          )}
        </>
      )}
      {savedViews.feedback}
      <SimpleModal
        isOpen={!!drawer}
        side="right"
        title={
          drawer?.kind === 'club' ? (
            <span className="slc-drawerTag">
              {[drawer.value.category || drawer.value.activity, drawer.value.scheduleLabel].filter(Boolean).join(' · ')}
            </span>
          ) : drawer?.kind === 'report' && drawer.scope === 'clubs' ? (
            'Clubs summary'
          ) : drawer?.kind === 'squady' ? (
            'Ask Squady'
          ) : drawer?.kind === 'student' ? (
            'Student participation'
          ) : drawer?.kind === 'how' ? (
            'How we count'
          ) : drawer?.kind === 'alert' ? (
            'Review signal'
          ) : drawer?.kind === 'save' ? (
            'Save view'
          ) : drawer?.kind === 'export' || drawer?.kind === 'participation-summary' ? (
            drawer.reportKind === 'clubs' || view === 'clubs' ? (
              'Clubs report'
            ) : drawer.reportKind === 'sport' || view === 'sport' ? (
              'Sport report'
            ) : (
              'Participation summary'
            )
          ) : (
            'SLT Insights'
          )
        }
        onCloseClick={() => {
          setDrawer(null);
          setDrawerError('');
        }}
        showFooter={false}
        customClass={
          drawer?.kind === 'participation-summary' || drawer?.kind === 'export'
            ? 'sli-drawer sli-clubDrawer'
            : drawer?.kind === 'student'
            ? 'sli-drawer sli-clubDrawer sli-studentDrawer'
            : drawer?.kind === 'club'
            ? 'sli-drawer sli-clubDrawer'
            : drawer?.kind === 'report' && drawer.scope === 'clubs'
            ? 'sli-drawer sli-clubsExport'
            : 'sli-drawer'
        }
        dialogStyle={{ width: 560, maxWidth: '100vw' }}
      >
        {drawer && (
          <div className="sli-drawerBody">
            {drawerBody()}
            {drawerError && (
              <p className="sli-error" role="alert">
                {drawerError}
              </p>
            )}
          </div>
        )}
      </SimpleModal>
    </div>
  );
}
